/* eslint-disable react/no-unescaped-entities */
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatApiErrorDetail } from "../../lib/api";
import { Button } from "../../components/ui/button";
import { WorryMap } from "../../components/WorryMap";
import { CentreHeader, useCentre } from "../../components/baobab/CentreHeader";
import { toast } from "sonner";
import { Users, Activity, Video, AlertTriangle, ChevronRight, UserPlus, Loader2, HeartPulse, Frown, CalendarX, Pill, Droplets, Scale } from "lucide-react";

const STATUS = {
  a_jour: { label: "À jour", cls: "bg-[#5CB85C]/15 text-[#2d7a2d]" },
  seance_manquee: { label: "Séance manquée", cls: "bg-[#D4AF5A]/20 text-[#8A6D2B]" },
  alerte: { label: "Alerte", cls: "bg-[#D9534F]/15 text-[#b23a36]" },
};
const ALERT_ICON = { tension: HeartPulse, humeur: Frown, seance: CalendarX, observance: Pill, glycemie: Droplets, poids: Scale, inquietude: AlertTriangle };

function Kpi({ icon: Icon, label, value, accent, testid }) {
  return (
    <div data-testid={testid} className="bg-white rounded-2xl border border-[#E4E0D5] p-4 flex items-center gap-3">
      <div className="w-11 h-11 rounded-xl grid place-items-center" style={{ backgroundColor: `${accent}1a`, color: accent }}><Icon size={22} /></div>
      <div>
        <div className="font-[Fraunces] text-2xl font-bold text-[#0A1426] leading-none">{value}</div>
        <div className="text-[11px] text-[#0A1426]/60 mt-1">{label}</div>
      </div>
    </div>
  );
}

export default function ProDashboard() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [linkEmail, setLinkEmail] = useState("");
  const [linking, setLinking] = useState(false);
  const [filter, setFilter] = useState("tous");

  const load = () => api.get("/therapist/dashboard").then(({ data }) => setData(data)).catch(() => null);
  useEffect(() => { load(); }, []);
  const me = useCentre();

  const link = async () => {
    if (!linkEmail) return;
    setLinking(true);
    try { await api.post("/therapist/patients/link", { email: linkEmail }); setLinkEmail(""); toast.success("Patient rattaché"); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    setLinking(false);
  };

  if (!data) return <div className="py-20 grid place-items-center"><Loader2 className="animate-spin text-[#2BA3A0]" /></div>;
  const k = data.indicateurs;
  const patients = data.patients.filter((p) => filter === "tous" || p.statut === filter);
  const allAlerts = data.patients.flatMap((p) => p.alertes.map((a) => ({ ...a, patient: p })));

  return (
    <div className="bc-in flex flex-col gap-6" data-testid="pro-dashboard">
      <CentreHeader me={me} />
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <div className="text-[11px] uppercase tracking-wider text-[#0A1426]/60 font-bold">Tableau de bord</div>
          <h1 className="font-[Fraunces] text-3xl font-bold text-[#0A1426]">Mes patients</h1>
        </div>
        <div data-testid="pro-plan-badge" className="text-[12px] bg-white border border-[#E4E0D5] rounded-full px-3 py-1.5 text-[#0A1426]">
          Forfait <b>{data.forfait.nom}</b> · {k.patients_actifs}/{data.forfait.patients_max >= 100000 ? "∞" : data.forfait.patients_max} patients
        </div>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <Kpi testid="kpi-patients" icon={Users} label="Patients actifs" value={k.patients_actifs} accent="#2BA3A0" />
        <Kpi testid="kpi-assiduite" icon={Activity} label="Assiduité aujourd'hui" value={`${k.assiduite_pct}%`} accent="#0A1426" />
        <Kpi testid="kpi-teleconsult" icon={Video} label="Téléconsultations du jour" value={k.teleconsultations_jour} accent="#3a8acb" />
        <Kpi testid="kpi-alertes" icon={AlertTriangle} label="Alertes" value={k.alertes} accent="#D9534F" />
      </div>

      <div className="grid lg:grid-cols-3 gap-5">
        <div className="lg:col-span-2 flex flex-col gap-3">
          <div className="flex items-center gap-2 flex-wrap">
            {["tous", "alerte", "seance_manquee", "a_jour"].map((f) => (
              <button key={f} data-testid={`filter-${f}`} onClick={() => setFilter(f)}
                className={`px-3 py-1.5 rounded-full text-[12px] font-bold ${filter === f ? "bg-[#0A1426] text-white" : "bg-white border border-[#E4E0D5] text-[#0A1426]"}`}>
                {f === "tous" ? "Tous" : STATUS[f].label}
              </button>
            ))}
          </div>
          {patients.map((p) => (
            <button key={p.id} data-testid={`patient-row-${p.id}`} onClick={() => navigate(`/pro/patients/${p.id}`)}
              className="bg-white rounded-2xl border border-[#E4E0D5] p-4 flex items-center gap-4 text-left hover:border-[#2BA3A0] transition-colors">
              <div className="w-12 h-12 rounded-full bg-[#F5F3EE] grid place-items-center font-[Fraunces] font-bold text-[#0A1426]">{p.nom[0]}</div>
              <div className="flex-1 min-w-0">
                <div className="font-bold text-[#0A1426]">{p.nom}</div>
                <div className="text-[12px] text-[#0A1426]/60 truncate">{p.diagnostic} · côté {p.cote_atteint} · autonomie {p.score_autonomie ?? "—"}/100</div>
                {p.alertes.length > 0 && (
                  <div className="flex gap-1 mt-1 flex-wrap">
                    {p.alertes.map((a, i) => { const I = ALERT_ICON[a.type]; return (
                      <span key={i} className={`inline-flex items-center gap-1 text-[10px] px-2 py-0.5 rounded-full ${a.niveau === "haut" ? "bg-[#D9534F]/15 text-[#b23a36]" : "bg-[#D4AF5A]/20 text-[#8A6D2B]"}`}><I size={10} />{a.texte}</span>
                    ); })}
                  </div>
                )}
              </div>
              <span className={`text-[11px] font-bold px-2.5 py-1 rounded-full ${STATUS[p.statut].cls}`}>{STATUS[p.statut].label}</span>
              <ChevronRight size={18} className="text-[#0A1426]/40" />
            </button>
          ))}
          {patients.length === 0 && <div className="text-sm text-[#0A1426]/60 py-6 text-center">Aucun patient dans ce filtre.</div>}

          <div className="bg-white rounded-2xl border border-dashed border-[#E4E0D5] p-4 flex flex-col sm:flex-row gap-2 sm:items-center">
            <div className="flex items-center gap-2 text-[#0A1426] font-bold text-sm"><UserPlus size={18} /> Rattacher un patient</div>
            <input data-testid="link-patient-email" value={linkEmail} onChange={(e) => setLinkEmail(e.target.value)} placeholder="e-mail du patient"
              className="flex-1 h-11 px-4 rounded-full border border-[#E4E0D5] text-sm text-[#0A1426] focus:outline-none focus:ring-2 focus:ring-[#2BA3A0]" />
            <Button data-testid="link-patient-btn" disabled={linking} onClick={link} className="h-11 rounded-full bg-[#2BA3A0] text-white font-bold">Rattacher</Button>
          </div>
        </div>

        <div className="flex flex-col gap-3">
          <WorryMap compact />
          <h2 className="text-[12px] uppercase tracking-wider text-[#0A1426]/60 font-bold">Alertes à traiter</h2>
          {allAlerts.length === 0 && <div className="text-sm text-[#0A1426]/60">Aucune alerte.</div>}
          {allAlerts.map((a, i) => { const I = ALERT_ICON[a.type]; return (
            <button key={i} data-testid={`alert-${a.patient.id}-${a.type}`} onClick={() => navigate(`/pro/patients/${a.patient.id}`)}
              className={`text-left rounded-2xl p-3 border flex gap-3 items-start ${a.niveau === "haut" ? "bg-[#D9534F]/8 border-[#D9534F]/30" : "bg-white border-[#E4E0D5]"}`}>
              <I size={18} className={a.niveau === "haut" ? "text-[#D9534F]" : "text-[#8A6D2B]"} />
              <div>
                <div className="text-[13px] font-bold text-[#0A1426]">{a.patient.nom}</div>
                <div className="text-[12px] text-[#0A1426]/70">{a.texte}</div>
              </div>
            </button>
          ); })}
        </div>
      </div>
    </div>
  );
}
